import { Children, cloneElement, type ComponentPropsWithoutRef, type MouseEventHandler, type ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { HStack } from '../Core';
import { ICON_SIZES, Icon, IconName } from '../Icons';
import { BreadcrumbItemButton, BreadcrumbItemCurrent, BreadcrumbItemLink, BreadcrumbWrapper } from './styles';

export { BreadcrumbWrapper } from './styles';

type BreadcrumbItemProps = {
  to?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  children?: ReactNode;
  $isLast?: boolean;
  $isFirst?: boolean;
} & Omit<ComponentPropsWithoutRef<'span'>, 'onClick' | 'children'>;

export function BreadcrumbItem({ to, onClick, children, $isLast, $isFirst: _$isFirst, ...rest }: BreadcrumbItemProps) {
  if (to) {
    return (
      <BreadcrumbItemLink to={to} onClick={onClick} $isLast={$isLast} {...rest}>
        {children}
      </BreadcrumbItemLink>
    );
  }
  if (onClick) {
    return (
      <BreadcrumbItemButton {...rest} type="button" onClick={onClick} $isLast={$isLast}>
        {children}
      </BreadcrumbItemButton>
    );
  }
  return (
    <BreadcrumbItemCurrent $isLast={$isLast} {...rest}>
      {children}
    </BreadcrumbItemCurrent>
  );
}

export const Breadcrumb = styled(({ children }: { children: JSX.Element[] }) => {
  const theme = useTheme();

  const elements = Children.map(children, (c, index) => {
    const isFirst = index === 0;
    const isLast = index === children.length - 1;
    return (
      <HStack key={index} overflow="hidden" minWidth="2rem" flex={isFirst || isLast ? '0 0 auto' : '0 1 auto'}>
        {cloneElement(c, { $isLast: isLast, $isFirst: isFirst })}
        {!isLast ? <Icon icon={IconName.ChevronRight} color={theme.colorTextDefault} size={ICON_SIZES.TINY} /> : null}
      </HStack>
    );
  });

  return <BreadcrumbWrapper>{elements}</BreadcrumbWrapper>;
})``;
